"use client";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AuthType, SignUpDataProps } from "@/interface/authentication";
import { hearAboutOptions } from "@/utils";
import axios from "axios";
import { AppDispatch, AppState } from "@/store/configureStore";
import { updateSignupUser } from "@/store/slices/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { REG_WITH_EMAIL_API, REG_WITH_NUMBER_API } from "@/constants";

type Props = {
	setRegistrationStatus: React.Dispatch<
		React.SetStateAction<"default" | "success" | "error">
	>;
	setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
	type: AuthType;
};

const useValidateSignup = ({ type, setRegistrationStatus, setErrorMessage }: Props) => {
	const dispatch = useDispatch<AppDispatch>();
	const user = useSelector((state: AppState) => state.user);

	const signupSchema = yup.object().shape({
		email:
			type === "email"
				? yup
						.string()
						.email("Invalid email address")
						.required("Email is required")
				: yup.string(),
		password: yup
			.string()
			.required("Password is required")
			.min(8, "must be at least 8 characters long"),
		phone_number:
			type === "phone_number"
				? yup.string().required("Phone number is required")
				: yup.string(),
		get_to_know: yup.string().required("This field is required"),
		fullName: yup.string().required("This field is required"),
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		resetField,
		setValue,
		clearErrors,
	} = useForm({
		resolver: yupResolver(signupSchema),
	});
	const [selectFieldValues, setSelectFieldValues] = useState<string>("");
	const onOptionChange = (option: string) => {
		setSelectFieldValues(option);
		clearErrors("get_to_know");
	};

	useEffect(() => {
		register("get_to_know");
		setValue("get_to_know", selectFieldValues);
	}, [register, selectFieldValues, setValue]);

	useEffect(() => {
		resetField(type === AuthType.EMAIL ? AuthType.PHONE_NUMBER : AuthType.EMAIL);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [type]);

	const handleSignup = async (data: SignUpDataProps) => {
		console.log("data: ", data);
		const endpoint =
			type === AuthType.EMAIL ? REG_WITH_EMAIL_API : REG_WITH_NUMBER_API;
		try {
			const response = await axios.post(endpoint, {
				fullName: data?.fullName,
				email: type === AuthType.EMAIL && data?.email,
				phoneNumber:
					type === AuthType.PHONE_NUMBER &&
					(data?.phone_number?.startsWith("0")
						? `+234${data?.phone_number?.slice(1)}`
						: `+234${data?.phone_number}`),
				password: data?.password,
			});
			console.log("API Response: ", response?.data);
			setRegistrationStatus("success");
			dispatch(
				updateSignupUser({
					id: response?.data?.user?.id,
					email: response?.data?.user?.email,
				})
			);
		} catch (error: string | any) {
			setRegistrationStatus("error");
			setErrorMessage(error.response.data.error);
			console.log("API Error: ", error);
		}
	};

	return {
		register,
		handleSubmit,
		errors,
		handleSignup,
		hearAboutOptions,
		onOptionChange,
	};
};

export default useValidateSignup;
